import jwtDecoder from 'jwt-decode';
import Cookies from 'js-cookie';
import { differenceInMinutes } from 'date-fns';
import throttle from 'lodash/throttle';
import { logger } from './logger';

/**
 * Token constants.
 */
const tokenKey = `Token${window.environment.ENVIRONMENT}`;
const domain =
    window.location.href.indexOf('localhost') > 0
        ? 'localhost'
        : '.' +
          window.location.origin
              .split('.')
              .slice(-2)
              .join('.')
              .replace(/(.*)(:.*)/, '$1'); // ex: '.carecontinuity.com'

/**
 * Returns the token key for the current environment.
 * @returns {string}
 */
export const getTokenKey = () => tokenKey;

/**
 * Sets the cookie token for the current domain.
 * @param {string} token
 */
export const setCookieToken = (token, tokenKeyOverride) => {
    Cookies.set(tokenKeyOverride ?? tokenKey, token, { domain });
};

export const deleteCookieToken = (tokenKeyOverride) => {
    Cookies.remove(tokenKeyOverride ?? tokenKey);
};

/**
 * Returns the cookie domain.
 * @returns {string}
 */
export const getCookieDomain = () => domain;

/**
 * For the provided token, returns the token expiration time in milliseconds.
 * @param {string} token
 * @returns {number}
 */
export const getJwtTokenExpireMillis = (token) => {
    if (!!token) {
        return jwtDecoder(token).exp * 1000 - new Date();
    } else {
        logger.warn('Cannot decode JWT token: No token provided');
    }
};

/**
 * Returns the current token expiration time in minutes.
 * @returns {number}
 */
export const getTokenExpiryInMin = (tokenKeyOverride) => {
    const cookieToken = Cookies.get(tokenKeyOverride ?? tokenKey, { domain });
    if (!cookieToken || cookieToken === 'undefined') {
        return null;
    }
    const { exp } = decodeToken(cookieToken);

    return differenceInMinutes(new Date(exp * 1000), new Date());
};

/**
 * Decodes the string token into a structured object.
 * @param {string} jwt - string token
 * @param {Object} options
 * @param {Object} options.complete - if true, returns header object
 * @returns {Object} returns either the parsed payload or the parsed header if options.complete is true
 */
export const decodeToken = (jwt, options) => {
    options = options || {};

    if (!jwt) {
        return null;
    }

    try {
        // jwt-decode handles payload parsing automatically
        var payload = jwtDecoder(jwt);

        // Return header if `complete` option is enabled
        // header includes claims such as `kid` and `alg`
        if (options.complete === true) {
            var header = jwtDecoder(jwt, { header: true });
            // Extract signature from the JWT (third part)
            var parts = jwt.split('.');
            var signature = parts.length === 3 ? parts[2] : null;
            return {
                header: header,
                payload: payload,
                signature: signature,
            };
        }
        return payload;
    } catch (e) {
        return null;
    }
};

/**
 * Returns the current token from a browser cookie for the current domain.
 * @returns {string}
 */
export const tokenResolver = (tokenKeyOverride) => {
    const cookieToken = Cookies.get(tokenKeyOverride ?? tokenKey, { domain });
    return cookieToken;
};

/**
 * Returns true if a token exists and has not expired, otherwise false.
 * @returns {boolean}
 */
export const checkValidTokenExists = () => {
    const token = tokenResolver();
    if (!token) return false;
    let expirationInMs;
    try {
        const { exp } = decodeToken(token);
        expirationInMs = exp;
    } catch {
        logger.warn('checkValidTokenExists: invalid token.');
        return false;
    }
    const isValid = new Date(expirationInMs * 1000) > new Date();
    return isValid;
};

// Throttled error logger - only logs once every 5 seconds
const logCookieError = throttle((tokenKey, domain) => {
    logger.error(`No "${tokenKey}" key found in cookie for domain "${domain}"`);
}, 5000);

/**
 * Returns the user ID from the cookie token if it exists.
 * @returns {number}
 */
export const getUserIdFromToken = () => {
    const cookieToken = tokenResolver();
    if (!cookieToken) {
        logCookieError(tokenKey, domain);
        return null;
    }
    return decodeToken(cookieToken)?.LoggedInUserId;
};
